/**
 * O "modo" do gerador de orçamento — Épico 9, Story 9.4.
 *
 * ## Por que um modo, e não uma segunda tela
 *
 * O cliente pediu uma **aba exclusiva** para o del credere, e é fácil ler isso
 * como "outra tela de orçamento". Seria o caminho errado: montar item, calcular
 * caixa fechada, desconto, células editáveis, emitir, gerar PDF — nada disso
 * muda no del credere. Duplicar o gerador significaria manter duas cópias da
 * lógica mais crítica do sistema, e a segunda cópia começa a divergir no
 * primeiro conserto que alguém esquecer de aplicar nas duas.
 *
 * O que é de fato exclusivo são duas coisas, e só elas:
 *
 * 1. **a porta de entrada** — menu próprio, lista própria, criação própria;
 * 2. **o seletor de coluna** por item, que não existe nos outros quadrantes.
 *
 * Então: mesmos componentes, montados em dois caminhos, com um modo que liga a
 * segunda coisa. A exclusividade é de navegação, não de motor.
 */

import { segmentoPorCode } from '@orc/shared';

/**
 * `comum` é RT10/CPF; `del_credere` é a seção própria; `vinilico` e `rodape`
 * são as duas seções da S5.
 *
 * ## As quatro não se marcam do mesmo jeito, e é assim mesmo
 *
 * `del_credere` sai do **quadrante** do orçamento — `DEL_CREDERE` é um
 * `table_type`. As duas da S5 saem do **segmento** (`quote.segmento`), porque
 * vinílico e rodapé são classes de produto, e classe é do item: não havia no
 * orçamento nada que dissesse de qual porta ele veio.
 *
 * Quem resolve essa assimetria num lugar só é `modoDoOrcamento`.
 */
export type ModoGerador = 'comum' | 'del_credere' | 'vinilico' | 'rodape';

/** Code do quadrante no backend. */
export const TABELA_DEL_CREDERE = 'DEL_CREDERE';

/**
 * O modo de um orçamento já carregado.
 *
 * Sai do **dado do próprio orçamento**, não da rota: um del credere aberto por
 * um link antigo `/orcamentos/:id` tem de mostrar o seletor do mesmo jeito. A
 * rota decide onde o usuário está; o dado decide o que a tela é.
 *
 * O quadrante vem primeiro de propósito: um orçamento del credere criado pela
 * seção de vinílicos é, antes de tudo, del credere — é o quadrante que manda no
 * preço e no seletor de coluna. O segmento só diz por qual porta ele entrou.
 */
export function modoDoOrcamento(tabela: string | undefined, segmento?: string | null): ModoGerador {
  if (tabela === TABELA_DEL_CREDERE) return 'del_credere';
  const s = segmentoPorCode(segmento);
  return s === undefined ? 'comum' : (s.code as ModoGerador);
}

/** Prefixo das rotas de cada modo — o menu fica ativo enquanto se navega. */
export function baseDoModo(modo: ModoGerador): string {
  if (modo === 'del_credere') return '/del-credere';
  const s = segmentoPorCode(modo);
  return s?.rota ?? '/orcamentos';
}

/** O segmento a gravar no orçamento — `null` na seção comum e no del credere. */
export function segmentoDoModo(modo: ModoGerador): string | null {
  return segmentoPorCode(modo)?.code ?? null;
}

/** A classe de produto que a busca oferece nesta seção. `undefined` = todas. */
export function classeDoModo(modo: ModoGerador): string | undefined {
  return segmentoPorCode(modo)?.classe;
}

/** O título da seção — o mesmo texto do menu. */
export function rotuloDoModo(modo: ModoGerador): string {
  if (modo === 'del_credere') return 'Del Credere';
  return segmentoPorCode(modo)?.rotulo ?? 'Orçamentos';
}

/** Uma coluna del credere, como o servidor a rotula. */
export interface NivelDelCredere {
  nivel: string;
  /** `5,5% · DEL 20%` — o texto que o vendedor lê. */
  rotulo: string;
  comissao: string;
  delCredere: string;
  /**
   * Onde a coluna fica na escala de PRECO — vem do servidor.
   *
   * Existe porque a tela de criacao escolhe o nivel **antes de haver item**:
   * sem produto nao ha preco a mostrar, e a posicao relativa e o que resolve a
   * confusao de rotulo sem precisar de um.
   */
  posicaoPreco: 'mais_barata' | 'intermediaria' | 'mais_cara';
}

/**
 * Os seis níveis, buscados do servidor.
 *
 * A tela de criação precisa deles antes de existir item nenhum, então não pode
 * tirá-los da resposta do orçamento. E **não** os escreve à mão: o pareamento
 * (`del_3` é comissão 7% / DEL 5%, a mais barata; `del_7` é 3% / DEL 30%, a mais
 * cara) tem uma definição só — `PARES_DEL_CREDERE`, em `packages/shared` — e é
 * a planilha da fábrica que manda.
 * Duas listas divergiriam, e o sintoma seria o preço certo com a etiqueta errada
 * — pior que um erro, porque parece certo.
 */
export async function buscarNiveisDelCredere(tabela?: string): Promise<NivelDelCredere[]> {
  const { obter } = await import('../lib/api.js');
  /*
   * Sem `tabela` a rota devolve o del credere, como sempre fez — S4. Com ela,
   * devolve a escala daquele quadrante (as seis comissões de CPF/RT10).
   */
  const query = tabela === undefined ? '' : `?tabela=${encodeURIComponent(tabela)}`;
  return obter<NivelDelCredere[]>(`/api/precificacao/niveis-del-credere${query}`);
}
